import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import type { TableSort } from '../lib/tableControls'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'

export function SortableHeader({ column, label, sort, onSort, className = '' }: {
  column: string; label: string; sort: TableSort; onSort: (column: string) => void; className?: string
}) {
  const active = sort.key === column
  const Icon = active ? sort.direction === 'asc' ? ArrowUp : ArrowDown : ArrowUpDown
  return <th aria-sort={active ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'} className={`text-left font-medium text-muted-foreground ${className}`}>
    <button type="button" onClick={() => onSort(column)} aria-label={`Sort by ${label.toLowerCase()}`}
      className="inline-flex min-h-11 items-center gap-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {label}<Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
    </button>
  </th>
}

export function TableFilter({ label, value, onChange, options, allLabel = 'All' }: {
  label: string; value: string; onChange: (value: string) => void; options: { value: string; label: string }[]; allLabel?: string
}) {
  return <Select value={value} onValueChange={onChange}>
    <SelectTrigger aria-label={label} className="min-h-11 w-full sm:w-44"><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">{allLabel}</SelectItem>{options.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
  </Select>
}

export function TableToolbar({ query, onQueryChange, label, count, total, active, onClear, children }: {
  query: string; onQueryChange: (query: string) => void; label: string; count: number; total: number;
  active: boolean; onClear: () => void; children?: ReactNode
}) {
  return <div className="flex min-w-0 flex-wrap items-center gap-2">
    <Input aria-label={`Search ${label}`} placeholder={`Search ${label}...`} value={query} onChange={event => onQueryChange(event.target.value)} className="min-h-11 w-full sm:w-64" />
    {children}
    <span role="status" className="text-xs text-muted-foreground">{count} of {total} results</span>
    {active && <Button type="button" variant="ghost" size="sm" className="min-h-11" onClick={onClear}>Clear filters</Button>}
  </div>
}

export function TableSortSelect({ sort, onChange, options }: { sort: TableSort; onChange: (sort: TableSort) => void; options: { value: string; label: string }[] }) {
  return <>
    <Select value={sort.key} onValueChange={key => onChange({ ...sort, key })}>
      <SelectTrigger aria-label="Sort by" className="min-h-11 w-full sm:w-44"><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
    </Select>
    <Button type="button" variant="outline" size="icon" className="min-h-11 min-w-11" aria-label={sort.direction === 'asc' ? 'Sort descending' : 'Sort ascending'} onClick={() => onChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}>
      {sort.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
    </Button>
  </>
}
